import parseISO from 'date-fns/parseISO';

import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import { TFunction } from '@lyearn/i18n';
import { EXTERNAL_CONTENT_TYPE_LABEL } from '@/modules/corporate/modules/external-content/views/ExternalContentEditor/components/Editor/const';
import { convertEnumToReadableString } from '@/modules/knowledge-asset/types';
import { PerformanceStatusEnum, ResourceTypeEnum } from '@/types/schema';

import { getContentDuration } from '../../components/ContentCard/utils/getContentDuration';
import { getContentProgress } from '../../components/ContentCard/utils/getContentProgress';
import RESOURCE_TYPE_LABEL_CONFIG from '../../config/label';
import { CardContentType } from '../types';

export const RESOURCE_EVENT_TYPES = [
  ResourceTypeEnum.LiveEventContent,
  ResourceTypeEnum.OfflineEventContent,
  ResourceTypeEnum.ExternalEventContent,
  ResourceTypeEnum.RecurringLiveEventContent,
  ResourceTypeEnum.RecurringLiveEventContent,
  ResourceTypeEnum.RecurringOfflineEventContent,
  ResourceTypeEnum.RecurringExternalEventContent,
];

export const getContentTitle = (content: CardContentType) => {
  if (content.type === ResourceTypeEnum.ExternalContent) {
    return EXTERNAL_CONTENT_TYPE_LABEL[content.externalContentType!];
  } else if (
    content.type === ResourceTypeEnum.KnowledgeAsset &&
    'knowledgeAssetSubType' in content
  ) {
    return convertEnumToReadableString(content.knowledgeAssetSubType as string);
  }

  return RESOURCE_TYPE_LABEL_CONFIG[content.type]?.title;
};

export const getContentDescription = (content: CardContentType) => {
  switch (content.type) {
    case ResourceTypeEnum.Quiz:
    case ResourceTypeEnum.Task: {
      return content.objective || content.instruction?.text;
    }
    case ResourceTypeEnum.Exercise: {
      return content.exerciseObjective || content.instruction?.text;
    }
    default: {
      return content.description || content.objective;
    }
  }
};

export const getQuizProgress = (status?: PerformanceStatusEnum) => {
  return status === PerformanceStatusEnum.Completed ? 100 : 0;
};

export const getContentLabel = (t: TFunction, content: CardContentType, duration: string) => {
  const { quizQuestionsConfig, startTime } = content;

  const title = getContentTitle(content);

  if (content.type === ResourceTypeEnum.KnowledgeAsset) {
    return `${title} • ${duration}`;
  }

  if (content.type === ResourceTypeEnum.Quiz) {
    return `${t(title)} • ${t('ContentCards.Question', {
      count: quizQuestionsConfig?.questionCount,
    })}`;
  }

  if (RESOURCE_EVENT_TYPES.includes(content.type)) {
    const isoStartTime = parseISO(new Date(startTime!).toISOString());

    return `${t('ResourceTypeSM.Session')} • ${formatDate(isoStartTime, DateFormat.DateTime)}`;
  }

  if (content.type === ResourceTypeEnum.Roleplay) {
    return content.conversationTimeLimit != null ? `${t(title)} • ${duration}` : t(title);
  }

  return content.duration === -1 ? t(title) : `${t(title)} • ${duration}`;
};

export const getContentCardProgress = (content: CardContentType) => {
  const { progress } = getContentProgress(content);

  if (content.type === ResourceTypeEnum.Quiz || content.type === ResourceTypeEnum.Roleplay) {
    return getQuizProgress(content.performance?.status);
  }

  return progress;
};

export const getContentCardDuration = (t: TFunction, content: CardContentType) => {
  const { duration } = getContentDuration(t, content);

  return content.type === ResourceTypeEnum.Quiz
    ? t('ContentCards.Question', { count: content.quizQuestionsConfig?.questionCount })
    : duration;
};
